import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["value", "disableFuture", "disablePast", "onPickerChange", "onCancel", "isoWeek", "format", "hasInput", "locale", "withFooter", "disabled", "inputProps", "id", "minDate", "maxDate", "allowSameDateSelection"];
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { EmbersInnerThemeProvider } from '../rtlProvider';
import { LocalizationProvider, StaticDateRangePicker } from './lab/src';
import { CustomDateAdapter, ArrowIcon } from './base';
import { datePickerTheme, DateRangeFooter, DateRangePickerDay, StyledDateRangeField, StyledDateRangeCalender } from './dateStyles';
import { getDateRange, getFormat, focusToPointedElement, onKeyUp, convertToMoment, dateAdapter } from '../utils/dateUtils';
import { useId } from '../utils/useId';
import { EmbersButton } from '../button/button';
import { EmbersDateField } from './dateField';
export var EmbersDateRangeCalendar = function EmbersDateRangeCalendar(props) {
  var value = props.value,
    _props$disableFuture = props.disableFuture,
    disableFuture = _props$disableFuture === void 0 ? true : _props$disableFuture,
    _props$disablePast = props.disablePast,
    disablePast = _props$disablePast === void 0 ? false : _props$disablePast,
    onPickerChange = props.onPickerChange,
    onCancel = props.onCancel,
    isoWeek = props.isoWeek,
    format = props.format,
    hasInput = props.hasInput,
    localeProp = props.locale,
    _props$withFooter = props.withFooter,
    withFooter = _props$withFooter === void 0 ? false : _props$withFooter,
    disabled = props.disabled,
    inputProps = props.inputProps,
    _id = props.id,
    minDate = props.minDate,
    maxDate = props.maxDate,
    _props$allowSameDateS = props.allowSameDateSelection,
    allowSameDateSelection = _props$allowSameDateS === void 0 ? true : _props$allowSameDateS,
    otherProps = _objectWithoutProperties(props, _excluded);
  var intl = useIntl();
  var id = useId(_id);
  var locale = localeProp || moment.locale();
  var dateFormat = getFormat(format, locale);
  var _React$useState = React.useState([null, null]),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    range = _React$useState2[0],
    setRange = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    startError = _React$useState4[0],
    setStartError = _React$useState4[1];
  var _React$useState5 = React.useState(false),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    endError = _React$useState6[0],
    setEndError = _React$useState6[1];
  var _React$useState7 = React.useState([false, false]),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    innerError = _React$useState8[0],
    setInnerError = _React$useState8[1];
  useEffect(function () {
    if (value) {
      setRange([value[0] ? moment(value[0]) : null, value[1] ? moment(value[1]) : null]);
    }
  }, [value]);
  var handleRangeSelect = function handleRangeSelect(value) {
    var bothFilled = !range.some(function (v) {
      return v === null;
    });
    if (bothFilled) {
      if (range[0] !== value[0]) {
        setRange([value[0], null]);
      } else {
        setRange(value);
      }
    } else {
      if (!range[0] && range[1]) {
        setRange([value[0], null]);
      } else {
        setRange(value);
      }
    }
    startError && setStartError(false);
    endError && setEndError(false);
  };
  var handlePickerSelect = function handlePickerSelect(order) {
    return function (_value) {
      var value = moment.isDate(_value) ? moment(_value) : _value;
      if (order === 'start') {
        // to judge the start date is after or equal end date, if `true` should show invalid date error
        if (value && range[1] && (allowSameDateSelection && value.isAfter(range[1]) || !allowSameDateSelection && (value.isAfter(range[1]) || value.isSame(range[1])))) {
          setStartError(true);
        } else {
          setRange([value, range[1]]);
          startError && setStartError(false);
          !innerError[1] && endError && setEndError(false);
        }
      } else {
        // to judge the end date is before or equal start date, if `true` should show invalid date error
        if (value && range[0] && (allowSameDateSelection && value.isBefore(range[0]) || !allowSameDateSelection && (value.isBefore(range[0]) || value.isSame(range[0])))) {
          setEndError(true);
        } else {
          setRange([range[0], value]);
          !innerError[0] && startError && setStartError(false);
          endError && setEndError(false);
        }
      }
    };
  };

  // the callback of inputting the disabled date or invalid format
  var handleError = function handleError(order) {
    return function (error) {
      if (order === 'start') {
        setInnerError([error, innerError[1]]);
      } else {
        setInnerError([innerError[0], error]);
      }
    };
  };
  var handleRangeApply = function handleRangeApply() {
    if (range[1]) {
      var _range$;
      onPickerChange === null || onPickerChange === void 0 || onPickerChange(getDateRange(7, range, isoWeek), [((_range$ = range[0]) === null || _range$ === void 0 ? void 0 : _range$.toDate()) || null, range[1].toDate()]);
    }
  };
  var renderPickerRange = function renderPickerRange(day, dateRangePickerDayProps) {
    return /*#__PURE__*/React.createElement(DateRangePickerDay, _extends({}, dateRangePickerDayProps, {
      disableRipple: true
    }));
  };
  var rangeAdapter = function rangeAdapter() {
    var startDate = dateAdapter(range[0], disableFuture, disablePast);
    var endDate = dateAdapter(range[1], disableFuture, disablePast);
    if (startError) {
      startDate = null;
    }
    if (endError) {
      endDate = null;
    }
    return [startDate, endDate];
  };
  return /*#__PURE__*/React.createElement(EmbersInnerThemeProvider, {
    theme: datePickerTheme
  }, /*#__PURE__*/React.createElement(LocalizationProvider, {
    dateAdapter: CustomDateAdapter,
    locale: locale
  }, /*#__PURE__*/React.createElement(StyledDateRangeCalender, {
    id: id,
    className: "EmbersDateRangeCalendar-root",
    onKeyUp: onKeyUp,
    onKeyDown: hasInput ? focusToPointedElement(withFooter ? "#".concat(id, " .EmbersDateRangeTypeFooter-root") : "#".concat(id, " .EmbersRangePicker-root"), "#".concat(id, " .EmbersDateRangeCalendar-dateRangeField input:first-child"), !withFooter ? 'MuiDateRangePickerDay-day' : undefined) : undefined
  }, !!hasInput && /*#__PURE__*/React.createElement(StyledDateRangeField, {
    className: "EmbersDateRangeCalendar-dateRangeField"
  }, /*#__PURE__*/React.createElement(EmbersDateField, _extends({
    label: intl.formatMessage({
      id: 'embers.date.range.startLabel',
      defaultMessage: 'Start date'
    }),
    key: "start-date",
    value: range[0],
    locale: locale,
    format: dateFormat,
    handlePickerChange: handlePickerSelect('start'),
    disabled: disabled,
    disableFuture: disableFuture,
    disablePast: disablePast,
    color: startError || innerError[0] ? 'error' : undefined,
    onError: handleError('start')
  }, inputProps === null || inputProps === void 0 ? void 0 : inputProps.start)), /*#__PURE__*/React.createElement("span", {
    className: "icon-arrow icon-arrow-right"
  }), /*#__PURE__*/React.createElement(EmbersDateField, _extends({
    label: intl.formatMessage({
      id: 'embers.date.range.endLabel',
      defaultMessage: 'End date'
    }),
    key: "end-date",
    value: range[1],
    locale: locale,
    format: dateFormat,
    handlePickerChange: handlePickerSelect('end'),
    disabled: disabled,
    disableFuture: disableFuture,
    disablePast: disablePast,
    color: endError || innerError[1] ? 'error' : undefined,
    onError: handleError('end')
  }, inputProps === null || inputProps === void 0 ? void 0 : inputProps.end))), /*#__PURE__*/React.createElement(StaticDateRangePicker, _extends({
    className: "EmbersRangePicker-root",
    displayStaticWrapperAs: "desktop",
    value: rangeAdapter(),
    onChange: handleRangeSelect,
    components: {
      LeftArrowIcon: ArrowIcon('left'),
      RightArrowIcon: ArrowIcon('right')
    },
    componentsProps: {
      leftArrowButton: {
        disableRipple: true,
        className: 'EmbersDateRangeCalendar-switchLeft'
      },
      rightArrowButton: {
        disableRipple: true,
        className: 'EmbersDateRangeCalendar-switchRight'
      }
    },
    renderDay: renderPickerRange,
    renderInput: function renderInput(startProps, endProps) {
      return /*#__PURE__*/React.createElement(React.Fragment, null);
    },
    disableCloseOnSelect: true,
    allowSameDateSelection: allowSameDateSelection,
    autoFocus: false,
    disableFuture: disableFuture,
    disablePast: disablePast,
    defaultCalendarMonth: moment(new Date()).subtract(1, 'month'),
    open: true,
    disabled: disabled,
    minDate: minDate ? convertToMoment(minDate) : undefined,
    maxDate: maxDate ? convertToMoment(maxDate) : undefined
  }, otherProps)), withFooter && /*#__PURE__*/React.createElement(DateRangeFooter, {
    className: "EmbersDateRangeTypeFooter-root"
  }, /*#__PURE__*/React.createElement("div", {
    className: "EmbersDateRangeTypeFooter-buttons"
  }, /*#__PURE__*/React.createElement(EmbersButton, {
    level: "secondary",
    drawingStyle: "outlined",
    size: "small",
    onClick: onCancel,
    disabled: disabled
  }, intl.formatMessage({
    id: 'embers.button.cancel',
    defaultMessage: 'Cancel'
  })), /*#__PURE__*/React.createElement(EmbersButton, {
    size: "small",
    disabled: disabled || !rangeAdapter()[0] || !rangeAdapter()[1],
    onClick: handleRangeApply
  }, intl.formatMessage({
    id: 'embers.button.apply',
    defaultMessage: 'Apply'
  })))))));
};